<template>
  <div class="flex justify-center">
    <ion-button 
      :disabled="!isAffordable(reward) || isRedeeming || isRedeemed"
      @click="handleRedeem"
      data-cy="redeem-button"
      class="h-12 px-6 mx-auto w-[280px]"
    >
      <div class="flex items-center justify-center">
        <template v-if="isRedeeming">
          <ion-spinner name="circular" class="w-6 h-6 mr-2 flex-shrink-0"></ion-spinner>
          <span class="whitespace-nowrap overflow-hidden text-ellipsis">{{ $t('rewards.redeeming') }}</span>
        </template>
        <template v-else-if="isRedeemed">
          <ion-icon :icon="checkmarkOutline" class="w-6 h-6 mr-2 flex-shrink-0"></ion-icon>
          <span class="whitespace-nowrap overflow-hidden text-ellipsis">{{ $t('rewards.redeemSuccess') }}</span>
        </template>
        <template v-else>
          <ion-icon 
            :icon="isAffordable(reward) ? checkmarkOutline : lockClosedOutline" 
            class="w-5 h-5 mr-2 flex-shrink-0"
          ></ion-icon>
          <span class="whitespace-nowrap overflow-hidden text-ellipsis">
            {{ isAffordable(reward) ? $t('rewards.redeem') : $t('rewards.notEnoughPoints') }}
          </span>
        </template>
      </div>
    </ion-button>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { IonButton, IonSpinner, IonIcon } from '@ionic/vue';
import { checkmarkOutline, lockClosedOutline } from 'ionicons/icons';
import type { Reward } from '../types';

const props = defineProps<{
  reward: Reward;
  isAffordable: (reward: Reward) => boolean;
  onRedeem: (reward: Reward) => Promise<boolean>;
}>();

const emit = defineEmits<{
  (_e: 'close'): void;
}>();

// State variables to track redemption status
const isRedeeming = ref(false);
const isRedeemed = ref(false);

async function handleRedeem() {
  if (!props.reward) return;
  
  // Set redeeming state
  isRedeeming.value = true;
  
  // Wait for 2 seconds to simulate processing
  setTimeout(async () => {
    if (!props.reward) {
      isRedeeming.value = false;
      return;
    }
    
    const success = await props.onRedeem(props.reward);
    
    if (success) {
      // Show success state for 2 seconds
      isRedeeming.value = false;
      isRedeemed.value = true;
      
      // Auto-close modal after 2 seconds
      setTimeout(() => {
        isRedeemed.value = false;
        emit('close');
      }, 2000);
    } else {
      // Reset state if redemption failed
      isRedeeming.value = false;
    }
  }, 2000);
}
</script> 